// Theme: Light / Dark / Auto (selectable, not just a start state). Auto sets
// no data-theme attribute so tokens.css follows prefers-color-scheme live; the
// choice is persisted in localStorage (every access guarded).
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export type Theme = 'light' | 'dark' | 'auto';
export type EffectiveTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'moneyflow_theme';

export function getStoredTheme(): Theme {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY);
    return v === 'light' || v === 'dark' || v === 'auto' ? v : 'auto';
  } catch {
    return 'auto';
  }
}

function store(t: Theme): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, t);
  } catch {
    /* storage blocked: the toggle still works for this session */
  }
}

function systemPref(): EffectiveTheme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

interface ThemeValue {
  theme: Theme;
  effective: EffectiveTheme;
  setTheme: (t: Theme) => void;
}

const Ctx = createContext<ThemeValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(getStoredTheme);
  const [effective, setEffective] = useState<EffectiveTheme>(() => (theme === 'auto' ? systemPref() : theme));

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'auto') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', theme);

    if (theme !== 'auto') {
      setEffective(theme);
      return undefined;
    }
    setEffective(systemPref());
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setEffective(systemPref());
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [theme]);

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t);
    store(t);
  }, []);

  const value = useMemo(() => ({ theme, effective, setTheme }), [theme, effective, setTheme]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTheme(): ThemeValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useTheme must be used within a ThemeProvider');
  return v;
}
